import gql from 'graphql-tag';
import { useMutation, type MutationHookOptions } from '@apollo/react-hooks';
import { createAtlAttributionHeader } from '../../utils';
import { type ProductContextKey } from './lib/realtime';
import type { DocumentNode } from 'graphql';

interface MutationVariables {
	context?: string;
	installationId: string;
	isGlobal?: boolean;
	name: string;
	payload: string;
	token?: string;
}

interface MutationData {
	ecosystem: {
		publishRealtimeChannel?: {
			eventId: string;
			eventTimestamp: string;
		};
	};
}

export type PublishRealtimeChannelPayload = {
	data: {
		channelName: string;
		eventPayload: string | Record<string, unknown>;
		isGlobal?: boolean;
		options?: {
			contextOverrides?: ProductContextKey[];
			token?: string;
		};
	};
};

export type PublishRealtimeChannel = (
	payload: PublishRealtimeChannelPayload,
	contextToken?: string,
) => Promise<{ eventId: string; eventTimestamp: string } | undefined>;

export const publishRealtimeChannel: DocumentNode = gql`
	mutation publishRealtimeChannel(
		$installationId: ID!
		$name: String!
		$payload: String!
		$context: String
		$isGlobal: Boolean
		$token: String
	) {
		ecosystem {
			publishRealtimeChannel(
				installationId: $installationId
				name: $name
				payload: $payload
				context: $context
				isGlobal: $isGlobal
				token: $token
			) {
				eventId
				eventTimestamp
			}
		}
	}
`;

export const usePublishRealtimeChannel = (
	installationId: string,
	context: string,
	options?: MutationHookOptions<MutationData, MutationVariables>,
) => {
	const [mutationFunction] = useMutation<MutationData, MutationVariables>(
		publishRealtimeChannel,
		options,
	);

	return async (
		payload: PublishRealtimeChannelPayload,
		contextToken?: string,
	): Promise<
		| {
				eventId: string;
				eventTimestamp: string;
		  }
		| undefined
	> => {
		const { channelName, isGlobal, options } = payload.data;

		const { token, contextOverrides } = options || {};

		const eventPayload = JSON.stringify(payload.data.eventPayload);
		const channelContext = contextOverrides
			? JSON.stringify({
					context,
					contextOverrides,
				})
			: context;

		const { data } = await mutationFunction({
			variables: {
				installationId,
				name: channelName,
				context: channelContext,
				payload: eventPayload,
				isGlobal: isGlobal ?? false,
				token,
			},
			context: {
				headers: {
					'atl-attribution': createAtlAttributionHeader(),
					...(contextToken ? { 'x-forge-context-token': contextToken } : {}),
				},
			},
		});

		if (data?.ecosystem.publishRealtimeChannel) {
			const { eventId, eventTimestamp } = data.ecosystem.publishRealtimeChannel;

			return {
				eventId,
				eventTimestamp,
			};
		}
	};
};
